// TODO: fix ordering of checkin and boarding lists

import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteButton from './DeleteButton';
import DeleteIconButton from './DeleteIconButton';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TableChartIcon from '@mui/icons-material/TableChart';
import ViewListIcon from '@mui/icons-material/ViewList';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import PropTypes from 'prop-types';
import React, { memo, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import RemoteAutocomplete from './RemoteAutocomplete';
import SyncWithLiveButton from './SyncWithLive';
import CheckinAndBoardingSection from '../trips/CheckinAndBoardingSection';
import CategoryCheckboxes from '../trips/CategoryCheckboxes';
import TagCategoryCompareTable from './TagCategoryCompareTable';
import TagCategoryTableView from './TagCategoryTableView';
import { usePreferences } from '../preferences';
import { ContextStore } from '../store/contextStore';
import { compareTagCategoriesWithDefaults, tourUsesOperatorDefaults } from '../utils/tagCategories';

const MAX_CHECKIN_TAG_CATEGORIES = 3;
const MAX_BOARDING_TAG_CATEGORIES = 1;

function tripLevelCategoryIdSet(tagCategories) {
  return new Set(
    (tagCategories || [])
      .filter((c) => c.category?.id)
      .map((c) => c.category.id.toString())
  );
}

function isDepartureCategory(tagCategory) {
  return tagCategory?.category?.code === 'departure';
}

function operatorDefaultsNotOnTrip(tagCategories, operatorDefaultCategories) {
  const ids = tripLevelCategoryIdSet(tagCategories);
  return (operatorDefaultCategories || []).filter(
    (cat) => !ids.has(cat.category?.id?.toString())
  );
}

// Defense in depth: never display more than one row for the same tag category.
// If more than one entry shares a category (e.g. leftover duplicate data), prefer
// a trip-level override (numeric group_tour) over an inherited one, and keep only
// the first entry among same-kind duplicates.
function dedupeTagCategoriesByCategory(tagCategories) {
  const seen = new Map();
  const withoutCategory = [];
  (tagCategories || []).forEach((cat) => {
    const id = cat.category?.id != null ? cat.category.id.toString() : null;
    if (!id) {
      withoutCategory.push(cat);
      return;
    }
    const existing = seen.get(id);
    if (!existing) {
      seen.set(id, cat);
      return;
    }
    const existingIsTripLevel = typeof existing.group_tour === 'number';
    const currentIsTripLevel = typeof cat.group_tour === 'number';
    if (currentIsTripLevel && !existingIsTripLevel) {
      seen.set(id, cat);
    }
  });
  return [...seen.values(), ...withoutCategory];
}

function capTripLevelCheckinBoardingSlots(tagCategories, operatorDefaultCategories, isInheritedCategory) {
  const notInTrip = operatorDefaultsNotOnTrip(tagCategories, operatorDefaultCategories);
  const inheritedCheckin =
    (tagCategories || []).filter((c) => isInheritedCategory(c) && c.checkin).length +
    notInTrip.filter((c) => c.checkin).length;
  const inheritedBoarding =
    (tagCategories || []).filter((c) => isInheritedCategory(c) && c.boarding).length +
    notInTrip.filter((c) => c.boarding).length;

  let checkinSlots = Math.max(0, MAX_CHECKIN_TAG_CATEGORIES - inheritedCheckin);
  let boardingSlots = Math.max(0, MAX_BOARDING_TAG_CATEGORIES - inheritedBoarding);

  return (tagCategories || []).map((cat) => {
    if (isInheritedCategory(cat)) return cat;
    const adjusted = { ...cat };
    let changed = false;
    if (adjusted.checkin) {
      if (checkinSlots <= 0) {
        adjusted.checkin = false;
        changed = true;
      } else {
        checkinSlots -= 1;
      }
    }
    if (adjusted.boarding) {
      if (boardingSlots <= 0) {
        adjusted.boarding = false;
        changed = true;
      } else {
        boardingSlots -= 1;
      }
    }
    return changed ? adjusted : cat;
  });
}

const useStyles = makeStyles(() => ({
  compactControlLabel: {
    '& .MuiFormControlLabel-label': {
      lineHeight: 1.4,
    },
    '& .MuiCheckbox-root, & .MuiRadio-root': {
      paddingTop: 4,
      paddingBottom: 4,
    },
  },
  categoryCard: {
    marginBottom: '1rem',
    width: '100%',
    border: '1px solid #D3D3D3',
    borderRadius: '8px',
    padding: '0.5rem 1rem',
  },
  operatorDefaultCard: {
    backgroundColor: '#f5f5f5',
    borderColor: '#e0e0e0',
  },
  tripOverrideCard: {
    backgroundColor: 'white',
  },
  sourceChip: {
    marginLeft: '0.5rem',
    height: '20px',
    fontSize: '0.7rem',
  },
}));

const TripDefaultSettings2 = (props) => {
  const classes = useStyles();
  const {
    mode = 'operator', // 'operator' | 'trip' | 'create'
    hasWritePerm = false,
    hasUpdateTagCategoriesPerm = false,
    hideGeneralSettings = false,

    // shared controls
    locationSharingValue,
    onLocationSharingChange,
    myTripsDisplayValue,
    onMyTripsDisplayChange,
    hasGroupChatValue,
    onHasGroupChatChange,
    excludeFromChatValue,
    onExcludeFromChatChange,
    // CP-8278: trip mode only -- the operator level already has this switch in
    // topconf (disable_internal_guides_and_staff_conversations).
    disableInternalChatValue,
    onDisableInternalChatChange,

    // operator defaults categories (for operator mode)
    defaultsAssociatedTagCategories = [],
    onSetDefaultsAssociated,
    // operator mode on a sandbox (sdtc WP-E): "Sync with live" for this operator, refetched by onLiveSynced
    // after the apply; disabled while the page has unsaved changes. Shown only when both are given.
    liveSyncOperatorId = null,
    onLiveSynced,
    formHasChanges = false,

    // trip mode extras
    trip,
    onTripChange,
    clientErrors,
    isParentTouched,
  } = props;
  const canManageCategories = hasWritePerm && (mode === 'operator' || hasUpdateTagCategoriesPerm);

  // Get fresh operator default categories from ContextStore (avoids stale cached data)
  const { tourOperator } = useContext(ContextStore);

  // Bespoke tours never collect a departure — has_departures-gated validation on the
  // backend explicitly ignores departure for bespoke tours (tour_user.py: "has_departures
  // and not gt.is_bespoke"). In trip mode, `trip.tag_categories` is already the backend's
  // *effective* set (TourEditSerializer.get_tag_categories -> get_effective_tag_categories()),
  // not a raw list of tour-owned rows — a departure entry with group_tour=null there is
  // always merged-in operator-default noise, never real per-tour data. So it's safe (and
  // necessary, CP-7812) to hide any departure category whenever the tour is bespoke, in
  // both create and trip mode.
  const hidesDepartureCategory = mode !== 'operator' && !!trip?.is_bespoke;

  // In trip mode or create mode, use fresh operator defaults from ContextStore instead of potentially stale trip.tag_categories_default
  const operatorDefaultCategories = useMemo(() => {
    let categories;
    if (mode === 'trip' || mode === 'create') {
      // Use fresh data from ContextStore
      categories = tourOperator?.default_tag_categories || [];
    } else {
      // In operator mode, use trip data (which is actually the operator defaults being edited)
      categories = trip?.tag_categories_default || [];
    }
    return hidesDepartureCategory ? categories.filter((cat) => !isDepartureCategory(cat)) : categories;
  }, [mode, tourOperator?.default_tag_categories, trip?.tag_categories_default, hidesDepartureCategory]);

  // Match backend tag_category_m2m._should_use_operator_defaults
  const usesOperatorDefaults = useMemo(
    () => (mode === 'operator' ? false : tourUsesOperatorDefaults(trip, tourOperator)),
    [mode, trip, tourOperator]
  );

  // Memoized: this array feeds the check-in/boarding capping effect, which writes back
  // into trip state. A fresh [] on every render made that effect re-run continuously.
  const activeOperatorDefaultCategories = useMemo(
    () => (usesOperatorDefaults ? operatorDefaultCategories : []),
    [usesOperatorDefaults, operatorDefaultCategories]
  );

  const { getPreference, setPreference } = usePreferences();
  const savedViewPref = getPreference('widget:TripDefaultSettings2', { view: 'table' });
  const [viewMode, setViewMode] = useState(savedViewPref?.view || 'table'); // 'cards' | 'table'
  const [categoryToDelete, setCategoryToDelete] = useState(null); // { idx, name, categoryId, followsDefault } for delete confirmation
  const [compareWithDefaults, setCompareWithDefaults] = useState(false);
  const checkinOrderContainerRef = useRef(null);
  const shouldCompensateScrollRef = useRef(false);
  const checkinOrderHeightBeforeRef = useRef(null);

  const handleViewModeChange = useCallback((e, newView) => {
    if (newView) {
      setViewMode(newView);
      setPreference('widget:TripDefaultSettings2', { view: newView });
    }
  }, [setPreference]);

  const isInheritedCategory = useCallback((cat) => {
    if (mode === 'operator' || !usesOperatorDefaults) return false;
    if (mode === 'create') return cat.group_tour === null || cat.group_tour === undefined;
    return typeof cat.group_tour !== 'number';
  }, [mode, usesOperatorDefaults]);

  // Guard against ever showing the same tag category more than once (e.g. a stray
  // duplicate trip-level row, or an inherited row alongside its own override).
  const dedupedTripTagCategories = useMemo(() => {
    const deduped = dedupeTagCategoriesByCategory(trip?.tag_categories);
    return hidesDepartureCategory ? deduped.filter((cat) => !isDepartureCategory(cat)) : deduped;
  }, [trip?.tag_categories, hidesDepartureCategory]);

  // CP-7968: the trip's own rows against the operator defaults (trip page only).
  const canCompareWithDefaults = mode === 'trip' && operatorDefaultCategories.length > 0;
  const comparedCategories = useMemo(
    () => (canCompareWithDefaults ? compareTagCategoriesWithDefaults(dedupedTripTagCategories, operatorDefaultCategories) : []),
    [canCompareWithDefaults, dedupedTripTagCategories, operatorDefaultCategories]
  );
  const showsComparison = canCompareWithDefaults && compareWithDefaults;

  // Determine which categories to work with based on mode
  const workingCategories = useMemo(() => {
    if (mode === 'operator') {
      return defaultsAssociatedTagCategories || [];
    }

    const cappedTripCategories = capTripLevelCheckinBoardingSlots(
      dedupedTripTagCategories,
      activeOperatorDefaultCategories,
      isInheritedCategory
    );

    if (!usesOperatorDefaults) {
      return cappedTripCategories;
    }

    const missingDefaults = operatorDefaultsNotOnTrip(dedupedTripTagCategories, operatorDefaultCategories).map((cat) => ({
      ...cat,
      idx: cat.idx || `op-default-${cat.category?.id || uuidv4()}`,
    }));

    return [...cappedTripCategories, ...missingDefaults];
  }, [
    mode,
    defaultsAssociatedTagCategories,
    dedupedTripTagCategories,
    operatorDefaultCategories,
    activeOperatorDefaultCategories,
    isInheritedCategory,
    usesOperatorDefaults,
  ]);

  // For trip/create mode: merge trip categories with operator defaults
  const mergedCategories = useMemo(() => {
    if (mode === 'operator') {
      return (defaultsAssociatedTagCategories || []).map((cat) => ({
        ...cat,
        source: 'operator',
        isOverride: false,
      }));
    }

    const cappedTrips = capTripLevelCheckinBoardingSlots(
      dedupedTripTagCategories,
      activeOperatorDefaultCategories,
      isInheritedCategory
    );

    const result = [];

    cappedTrips.forEach((cappedCat) => {
      const isInherited = isInheritedCategory(cappedCat);
      result.push({
        ...cappedCat,
        source: isInherited ? 'operator' : 'trip',
        isOverride: false,
        isInTrip: true,
      });
    });

    if (usesOperatorDefaults) {
      const notInTrip = operatorDefaultsNotOnTrip(dedupedTripTagCategories, operatorDefaultCategories);
      notInTrip.forEach((cat) => {
        result.push({
          ...cat,
          source: 'operator',
          isOverride: false,
          isInTrip: false,
          idx: cat.idx || `op-${cat.category?.id || uuidv4()}`,
        });
      });
    }

    return result.sort((a, b) => {
      const nameA = (a.category?.name || '').toLowerCase();
      const nameB = (b.category?.name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [
    mode,
    defaultsAssociatedTagCategories,
    dedupedTripTagCategories,
    operatorDefaultCategories,
    activeOperatorDefaultCategories,
    isInheritedCategory,
    usesOperatorDefaults,
  ]);

  // Ensure all categories have idx
  useEffect(() => {
    if (mode === 'operator') {
      const needsUpdate = workingCategories.some(cat => !cat.idx);
      if (needsUpdate) {
        const newCategories = workingCategories.map(cat => ({
          ...cat,
          idx: cat.idx ?? uuidv4()
        }));
        onSetDefaultsAssociated && onSetDefaultsAssociated(newCategories);
      }
    } else if (trip) {
      // Both 'trip' and 'create' modes use onTripChange
      const needsUpdate = trip.tag_categories.some(cat => !cat.idx);
      if (needsUpdate) {
        const newTagCategories = trip.tag_categories.map(cat => ({
          ...cat,
          idx: cat.idx ?? uuidv4()
        }));
        onTripChange((prevState) => ({ ...prevState, tag_categories: newTagCategories }));
      }
    }
  }, [mode, workingCategories, trip, onSetDefaultsAssociated, onTripChange]);

  useEffect(() => {
    if (mode !== 'trip' && mode !== 'create') return;
    if (!onTripChange) return;
    onTripChange((prev) => {
      const tags = prev.tag_categories;
      if (!tags?.length) return prev;
      const capped = capTripLevelCheckinBoardingSlots(
        tags,
        activeOperatorDefaultCategories,
        isInheritedCategory
      );
      const needsUpdate = capped.some((cat, i) => {
        const orig = tags[i];
        if (cat === orig) return false;
        return orig.checkin !== cat.checkin || orig.boarding !== cat.boarding;
      });
      if (!needsUpdate) return prev;
      return { ...prev, tag_categories: capped };
    });
  }, [mode, activeOperatorDefaultCategories, isInheritedCategory, onTripChange, trip?.tag_categories]);

  useLayoutEffect(() => {
    if (!shouldCompensateScrollRef.current) return;
    if (mode !== 'trip' && mode !== 'create') return;
    const container = checkinOrderContainerRef.current;
    const beforeHeight = checkinOrderHeightBeforeRef.current;
    if (!container || beforeHeight === null || beforeHeight === undefined) {
      shouldCompensateScrollRef.current = false;
      checkinOrderHeightBeforeRef.current = null;
      return;
    }

    const afterHeight = container.getBoundingClientRect().height;
    const diff = afterHeight - beforeHeight;
    if (diff !== 0) {
      // This is a deliberate "sharp" compensation to avoid visible jump.
      window.scrollBy(0, diff);
    }

    shouldCompensateScrollRef.current = false;
    checkinOrderHeightBeforeRef.current = null;
  }, [trip?.tag_categories, mode]);

  const checkinCategoriesCount = useMemo(() =>
    workingCategories.filter(category => category.checkin).length,
    [workingCategories]);

  const boardingCategoriesCount = useMemo(() =>
    workingCategories.filter(category => category.boarding).length,
    [workingCategories]);

  // Handler for changes - routes to appropriate callback
  const handleChange = useCallback((updater) => {
    if (mode === 'operator') {
      const prev = { id: 'operator-defaults', is_bespoke: false, tag_categories: defaultsAssociatedTagCategories || [] };
      const next = typeof updater === 'function' ? updater(prev) : updater;
      onSetDefaultsAssociated && onSetDefaultsAssociated(next.tag_categories);
    } else {
      onTripChange(updater);
    }
  }, [mode, defaultsAssociatedTagCategories, onSetDefaultsAssociated, onTripChange]);

  const onAssociatedTagCategoryAdd = useCallback(() => {
    if (!canManageCategories) return;
    handleChange((prevState) => {
      const new_category = {
        idx: uuidv4(),
        mode: 'trip',
        sequence: Math.max(...prevState.tag_categories.map(cat => cat.sequence ?? 0), -1) + 1,
        category: null,
        chat_conversation: false,
        group_tour: prevState.id,
        guides_from_tags: false,
        location_sharing: false,
        location_all_if_no_tag: false,
        max_tags: 0,
        min_tags: 0,
        user_association: false,
        user_selected: false,
        checkin: false,
        boarding: false,
        copy_tags_to_subbookings: true,
        is_new: true,
      };
      return { ...prevState, tag_categories: [...prevState.tag_categories, new_category] };
    });
  }, [canManageCategories, handleChange]);

  const onAssociatedTagCategoryChange = useCallback((ev, selected_category, targetIdx) => {
    handleChange((prevState) => {
      if (selected_category?.id) {
        const selectedId = selected_category.id.toString();
        const existingEntry = prevState.tag_categories.find((c) =>
          c.idx !== targetIdx &&
          c.category?.id?.toString() === selectedId
        );

        if (existingEntry) {
          // Check if the existing entry is an inherited operator default (identical settings)
          const matchingOpDefault = usesOperatorDefaults && typeof existingEntry.group_tour !== 'number';

          if (matchingOpDefault) {
            // Remove the inherited entry and allow the new one to replace it
            const filteredCategories = prevState.tag_categories.filter(c => c.idx !== existingEntry.idx);
            const tag_categories = filteredCategories.map((atc) =>
              atc.idx === targetIdx ? { ...atc, category: selected_category } : atc
            );
            return { ...prevState, tag_categories };
          }

          // It's a true duplicate (trip-level category), block the change
          return prevState;
        }
      }
      const tag_categories = prevState.tag_categories.map((atc) =>
        atc.idx === targetIdx ? { ...atc, category: selected_category } : atc
      );
      return { ...prevState, tag_categories };
    });
  }, [handleChange, usesOperatorDefaults]);

  const onAssociatedCategoryCheckboxChange = useCallback((ev, idx, field) => {
    ev.stopPropagation();
    handleChange((prevState) => {
      let update = prevState.tag_categories.filter((atc) => atc.idx === idx)[0];
      let removedOld = prevState.tag_categories.filter((atc) => atc.idx !== idx);
      if (field === 'required') {
        update = { ...update, min_tags: ev.target.checked ? 1 : 0 };
      }
      if (field === 'guides_from_tags') {
        update = { ...update, guides_from_tags: ev.target.checked };
      }
      if (field === 'guides_from_tags' && ev.target.checked) {
        update = { ...update, user_association: false, guides_from_tags: ev.target.checked };
      }
      if (field === 'user_association' && ev.target.checked) {
        update = { ...update, user_association: ev.target.checked, guides_from_tags: false };
      }
      if (field === 'chat_conversation') {
        update = { ...update, chat_conversation: ev.target.checked };
      }
      if (field === 'visible_in_chat') {
        update = { ...update, visible_in_chat: ev.target.checked };
      }
      if (field === 'user_association') {
        update = { ...update, user_association: ev.target.checked };
      }
      if (field === 'location_sharing') {
        if (ev.target.checked === false) {
          update = { ...update, location_sharing: ev.target.checked, user_association: false };
        } else {
          update = { ...update, location_sharing: ev.target.checked };
        }
      }
      if (field === 'user_selected') {
        update = { ...update, user_selected: ev.target.checked };
      }
      if (field === 'location_all_if_no_tag') {
        update = { ...update, location_all_if_no_tag: ev.target.checked };
      }
      if (field === 'max_tags') {
        update = { ...update, max_tags: ev.target.checked ? 1 : 0 };
      }
      if (field === 'allow_multiple_tags') {
        update = { ...update, max_tags: ev.target.checked ? 32767 : 0 };
      }
      if (field === 'remove_upon_date_changes') {
        update = { ...update, remove_upon_date_changes: ev.target.checked };
      }
      if (field === 'cleanup_on_assignment') {
        update = { ...update, cleanup_on_assignment: ev.target.checked };
      }
      if (field === 'departure_wide') {
        update = { ...update, departure_wide: ev.target.checked };
      }
      if (field === 'departure_key') {
        update = { ...update, departure_key: ev.target.checked };
      }
      if (field === 'remove_guides_on_cleanup') {
        update = { ...update, remove_guides_on_cleanup: ev.target.checked };
      }
      if (field === 'remove_unreferenced_guides') {
        update = { ...update, remove_unreferenced_guides: ev.target.checked };
      }
      if (field === 'copy_tags_to_guides') {
        update = { ...update, copy_tags_to_guides: ev.target.checked };
      }
      if (field === 'copy_tags_to_subbookings') {
        update = { ...update, copy_tags_to_subbookings: ev.target.checked };
      }
      if (field === 'visible_on_bookingmgmt') {
        update = { ...update, visible_on_bookingmgmt: ev.target.checked };
      }
      if (field === 'checkin_display') {
        update = { ...update, checkin_display: ev.target.checked };
      }
      return { ...prevState, tag_categories: [...removedOld, update] };
    });
  }, [handleChange]);

  const onCheckinBoardingCheckboxChange = useCallback((ev, idx, id, field) => {
    ev.stopPropagation();

    // When the "Check-in Tag Categories Order" block is expanded, toggling check-in/boarding
    // can change its height and make the browser "jump" the viewport.
    // We compensate by adjusting scroll by the delta height (no smooth scrolling).
    if (mode === 'trip' || mode === 'create') {
      const container = checkinOrderContainerRef.current;
      if (container) {
        shouldCompensateScrollRef.current = true;
        checkinOrderHeightBeforeRef.current = container.getBoundingClientRect().height;
      }
    }

    handleChange((prevState) => {
      const rowMatches = (atc) =>
        idx !== undefined && idx !== null ? atc.idx === idx : id != null && atc.id === id;

      const target = prevState.tag_categories.find(rowMatches);
      if (!target) return prevState;

      const updatedTagCategories = prevState.tag_categories.map((atc) =>
        rowMatches(atc) ? { ...atc, [field]: ev.target.checked } : atc
      );

      return { ...prevState, tag_categories: updatedTagCategories };
    });
  }, [handleChange, mode]);

  const onAssociatedCategoryDeleteRequest = useCallback((targetIdx) => {
    // The grid renders `mergedCategories`, which is also the idx namespace the delete
    // button reports; `workingCategories` is only a fallback (it mints different idx
    // values for inherited rows, so a lookup there can miss).
    const category =
      mergedCategories.find(cat => cat.idx === targetIdx) ||
      workingCategories.find(cat => cat.idx === targetIdx);
    const categoryName = category?.category?.name || 'this category';
    const categoryId = category?.category?.id != null ? category.category.id.toString() : null;
    // A trip row deleted while the trip uses the defaults hands its category to the default.
    const followsDefault =
      mode === 'trip' &&
      usesOperatorDefaults &&
      typeof category?.group_tour === 'number' &&
      !!categoryId &&
      operatorDefaultCategories.some((d) => d.category?.id?.toString() === categoryId);
    setCategoryToDelete({ idx: targetIdx, name: categoryName, categoryId, followsDefault });
  }, [mergedCategories, workingCategories, mode, usesOperatorDefaults, operatorDefaultCategories]);

  const onAssociatedCategoryDeleteConfirm = useCallback(() => {
    if (!categoryToDelete) return;
    const { idx: targetIdx, categoryId } = categoryToDelete;
    // Rows the grid shows, each with its own delete button.
    const shownIdx = new Set(mergedCategories.map((cat) => cat.idx));
    handleChange((prevState) => {
      // Drop the clicked row *and* any same-category duplicate that
      // dedupeTagCategoriesByCategory folded out of the grid. Filtering by idx alone
      // left the hidden twin in state, so it was saved back and the category
      // "reappeared" after reload (CP-7983). A duplicate the grid does show is kept:
      // the operator page folds nothing, and deleting one copy there removed them all
      // (CP-8511). Rows without a category (freshly added, nothing picked yet) can only
      // be matched by idx.
      const items = prevState.tag_categories.filter((atc) => {
        if (atc.idx === targetIdx) return false;
        if (categoryId && atc.category?.id?.toString() === categoryId && !shownIdx.has(atc.idx)) return false;
        return true;
      });
      return { ...prevState, tag_categories: items };
    });
    setCategoryToDelete(null);
  }, [handleChange, categoryToDelete, mergedCategories]);

  const onAssociatedCategoryDeleteCancel = useCallback(() => {
    setCategoryToDelete(null);
  }, []);

  // Create override from operator default (trip or create mode only)
  // Works exactly like onAssociatedTagCategoryAdd but with category and flags pre-filled
  const onCreateOverride = useCallback((operatorCategory) => {
    if (!canManageCategories) return;
    if (mode !== 'trip' && mode !== 'create') return;

    handleChange((prevState) => {
      const overrideCategoryId = operatorCategory.category?.id?.toString();

      // A trip-level override for this category may already exist (e.g. a previous
      // click already created one). Never create a second one for the same category.
      const alreadyHasOverride = overrideCategoryId && prevState.tag_categories.some(
        (tc) => tc.category?.id?.toString() === overrideCategoryId && typeof tc.group_tour === 'number'
      );
      if (alreadyHasOverride) {
        return prevState;
      }

      const new_category = {
        idx: uuidv4(),
        sequence: Math.max(...prevState.tag_categories.map(cat => cat.sequence ?? 0), -1) + 1,
        category: operatorCategory.category,
        chat_conversation: operatorCategory.chat_conversation ?? false,
        group_tour: prevState.id,
        guides_from_tags: operatorCategory.guides_from_tags ?? false,
        location_sharing: operatorCategory.location_sharing ?? false,
        location_all_if_no_tag: operatorCategory.location_all_if_no_tag ?? false,
        max_tags: operatorCategory.max_tags ?? 0,
        min_tags: operatorCategory.min_tags ?? 0,
        user_association: operatorCategory.user_association ?? false,
        user_selected: operatorCategory.user_selected ?? false,
        checkin: operatorCategory.checkin ?? false,
        boarding: operatorCategory.boarding ?? false,
        copy_tags_to_subbookings: operatorCategory.copy_tags_to_subbookings ?? true,
        visible_in_chat: operatorCategory.visible_in_chat ?? false,
        remove_upon_date_changes: operatorCategory.remove_upon_date_changes ?? false,
        cleanup_on_assignment: operatorCategory.cleanup_on_assignment ?? false,
        departure_wide: operatorCategory.departure_wide ?? false,
        remove_guides_on_cleanup: operatorCategory.remove_guides_on_cleanup ?? false,
        remove_unreferenced_guides: operatorCategory.remove_unreferenced_guides ?? false,
        copy_tags_to_guides: operatorCategory.copy_tags_to_guides ?? false,
        checkin_display: operatorCategory.checkin_display ?? false,
        is_new: true,
      };
      // Remove any inherited entry with the same category ID to prevent duplication
      const filteredCategories = overrideCategoryId
        ? prevState.tag_categories.filter(
            (tc) => tc.category?.id?.toString() !== overrideCategoryId
          )
        : prevState.tag_categories;
      return { ...prevState, tag_categories: [...filteredCategories, new_category] };
    });
  }, [canManageCategories, mode, handleChange]);

  const locationSharingScopeLabel = (mode === 'trip' || mode === 'create') ? 'for this tour' : 'for all tours';

  const tripObject = useMemo(() => {
    if (mode === 'operator') {
      return { id: 'operator-defaults', is_bespoke: false, tag_categories: defaultsAssociatedTagCategories || [] };
    }
    // Both 'trip' and 'create' modes use trip object with working categories
    return { ...trip, tag_categories: workingCategories };
  }, [mode, defaultsAssociatedTagCategories, trip, workingCategories]);

  // Filter categories for display (exclude empty checkin/boarding rows)
  const displayCategories = useMemo(() =>
    mergedCategories.filter(cat => {
      if ((cat.checkin || cat.boarding) && !cat.category?.id) {
        return false;
      }
      return true;
    }),
    [mergedCategories]
  );

  // "Disable All Chat Channels" (exclude_from_chat) and "Disable Internal Chat" are
  // per-tour kill switches: the backend has no operator default for them and reads the
  // tour's own flag at runtime (Group_Tour.save_chat_channel_and_threads tears every
  // channel down on exclude_from_chat). Hiding them behind "Use Tour Operator default
  // settings" left a previously-ticked flag silently in force with no visible control,
  // so they render in trip mode regardless of hideGeneralSettings, and never in
  // operator mode.
  const showTourChatOverrides = mode === 'trip';
  const showGroupChatBlock = !hideGeneralSettings || showTourChatOverrides;
  // SyncWithLiveButton itself renders only on a sandbox.
  const offersLiveSync = mode === 'operator' && liveSyncOperatorId != null && !!onLiveSynced;

  return (
    <>
      {!hideGeneralSettings && (
        <Grid item xs={12} md={6}>
          <Typography fontWeight={500}>Location Sharing</Typography>
          <FormControl fullWidth variant="filled" size="small" disabled={!hasWritePerm}>
            <RadioGroup
              name="defaults_location_sharing"
              value={(locationSharingValue ?? '').toString()}
              onChange={onLocationSharingChange}
            >
              <FormControlLabel className={classes.compactControlLabel} value="0" control={<Radio color="primary" size="small" />} label={`Location sharing is disabled ${locationSharingScopeLabel}`} />
              <FormControlLabel className={classes.compactControlLabel} value="1" control={<Radio color="primary" size="small" />} label={`Location sharing is enabled, but is not important ${locationSharingScopeLabel}`} />
              <FormControlLabel className={classes.compactControlLabel} value="2" control={<Radio color="primary" size="small" />} label={`Location sharing is important ${locationSharingScopeLabel}`} />
            </RadioGroup>
          </FormControl>
        </Grid>
      )}

      {showGroupChatBlock && (
        <Grid item xs={12} md={6}>
          <Typography fontWeight={500}>Group Chat</Typography>
          <FormGroup>
            {!hideGeneralSettings && (
              <FormControlLabel className={classes.compactControlLabel} control={<Checkbox name="has_group_chat" checked={!!hasGroupChatValue} onChange={onHasGroupChatChange} />} disabled={!hasWritePerm} label="Enable Group Chat" />
            )}
            {showTourChatOverrides && (
              <>
                <FormControlLabel className={classes.compactControlLabel} control={<Checkbox name="exclude_from_chat" checked={!!excludeFromChatValue} onChange={onExcludeFromChatChange} />} disabled={!hasWritePerm} label="Disable All Chat Channels" />
                <FormControlLabel className={classes.compactControlLabel} control={<Checkbox name="disable_internal_chat" checked={!!disableInternalChatValue} onChange={onDisableInternalChatChange} />} disabled={!hasWritePerm} label="Disable Internal Chat" />
              </>
            )}
          </FormGroup>
          {showTourChatOverrides && (
            <Typography variant="caption" color="textSecondary">
              Chat disable switches apply to this trip only and are not inherited from Tour Operator defaults.
            </Typography>
          )}
        </Grid>
      )}

      {!hideGeneralSettings && (
        <Grid item xs={12}>
          <FormControlLabel className={classes.compactControlLabel} control={<Checkbox name="mytrips_display" checked={!!myTripsDisplayValue} onChange={onMyTripsDisplayChange} />} disabled={!hasWritePerm} label="Display bookings on the My Trips screen" />
        </Grid>
      )}

      {showGroupChatBlock && (
        <Grid item xs={12}>
          <Divider sx={{ marginTop: '1rem', marginBottom: '1rem' }} />
        </Grid>
      )}

      <Grid item xs={12} ref={checkinOrderContainerRef}>
        <CheckinAndBoardingSection
          trip={tripObject}
          mode={mode}
          hasWritePerm={hasWritePerm}
          onChange={handleChange}
          clientErrors={clientErrors || {}}
          isParentTouched={isParentTouched}
        />
      </Grid>

      <Grid item xs={12}>
        <Divider sx={{ marginTop: '1rem', marginBottom: '1rem' }} />
      </Grid>

      {/* Associated Tag Categories Section */}
      <Grid item xs={12}>
        <Grid container direction="row" spacing={2} alignItems="center" justifyContent="space-between" sx={{ marginBottom: '1rem' }}>
          <Grid item>
            <Typography fontWeight={500}>Associated Tag Categories</Typography>
          </Grid>
          <Grid item sx={offersLiveSync ? { display: 'flex', alignItems: 'center', gap: 1 } : undefined}>
            {offersLiveSync && (
              <SyncWithLiveButton
                operatorId={liveSyncOperatorId}
                formHasChanges={formHasChanges}
                onApplied={onLiveSynced}
                categories={defaultsAssociatedTagCategories}
              />
            )}
            {canCompareWithDefaults && (
              <FormControlLabel
                className={classes.compactControlLabel}
                control={
                  <Switch
                    size="small"
                    checked={compareWithDefaults}
                    onChange={(ev) => setCompareWithDefaults(ev.target.checked)}
                    name="compare_with_operator_defaults"
                  />
                }
                label="Compare with Tour Operator defaults"
              />
            )}
            {!showsComparison && (
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={handleViewModeChange}
                size="small"
              >
                <ToggleButton value="cards" aria-label="cards view">
                  <Tooltip title="Cards View">
                    <ViewListIcon fontSize="small" />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="table" aria-label="table view">
                  <Tooltip title="Table View">
                    <TableChartIcon fontSize="small" />
                  </Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>
            )}
          </Grid>
        </Grid>

        {showsComparison ? (
          <TagCategoryCompareTable
            rows={comparedCategories}
            usesOperatorDefaults={usesOperatorDefaults}
            canDelete={canManageCategories}
            onDelete={onAssociatedCategoryDeleteRequest}
          />
        ) : viewMode === 'cards' ? (
          <CardsView
            categories={displayCategories}
            mode={mode}
            hasWritePerm={hasWritePerm}
            hasUpdateTagCategoriesPerm={hasUpdateTagCategoriesPerm}
            tripObject={tripObject}
            isParentTouched={isParentTouched}
            checkinCategoriesCount={checkinCategoriesCount}
            boardingCategoriesCount={boardingCategoriesCount}
            onAssociatedTagCategoryChange={onAssociatedTagCategoryChange}
            onAssociatedCategoryCheckboxChange={onAssociatedCategoryCheckboxChange}
            onCheckinBoardingCheckboxChange={onCheckinBoardingCheckboxChange}
            onAssociatedCategoryDelete={onAssociatedCategoryDeleteRequest}
            onCreateOverride={onCreateOverride}
            classes={classes}
          />
        ) : (
          <TagCategoryTableView
            categories={displayCategories}
            mode={mode}
            hasWritePerm={hasWritePerm}
            hasUpdateTagCategoriesPerm={hasUpdateTagCategoriesPerm}
            tripObject={tripObject}
            isParentTouched={isParentTouched}
            checkinCategoriesCount={checkinCategoriesCount}
            boardingCategoriesCount={boardingCategoriesCount}
            onAssociatedTagCategoryChange={onAssociatedTagCategoryChange}
            onAssociatedCategoryCheckboxChange={onAssociatedCategoryCheckboxChange}
            onCheckinBoardingCheckboxChange={onCheckinBoardingCheckboxChange}
            onAssociatedCategoryDelete={onAssociatedCategoryDeleteRequest}
            onCreateOverride={onCreateOverride}
          />
        )}

        {!showsComparison && (
          <Grid item xs={12} sx={{ marginTop: '1rem' }}>
            <Button
              onClick={onAssociatedTagCategoryAdd}
              color="primary"
              variant="outlined"
              startIcon={<AddIcon />}
              disabled={!canManageCategories}
            >
              Add Category
            </Button>
          </Grid>
        )}
      </Grid>

      {/* Delete confirmation dialog */}
      <Dialog
        open={!!categoryToDelete}
        onClose={onAssociatedCategoryDeleteCancel}
        aria-labelledby="delete-category-dialog-title"
      >
        <DialogTitle id="delete-category-dialog-title">Delete Tag Category</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete &quot;{categoryToDelete?.name}&quot;?
          </DialogContentText>
          {categoryToDelete?.followsDefault && (
            <DialogContentText sx={{ marginTop: '0.5rem' }}>
              This trip will then use the Tour Operator default for this category.
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onAssociatedCategoryDeleteCancel} color="primary">
            Cancel
          </Button>
          <DeleteButton onClick={onAssociatedCategoryDeleteConfirm}>
            Delete
          </DeleteButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

// Cards View Component (without drag-and-drop)
const CardsView = memo(({
  categories,
  mode,
  hasWritePerm,
  hasUpdateTagCategoriesPerm,
  tripObject,
  isParentTouched,
  checkinCategoriesCount,
  boardingCategoriesCount,
  onAssociatedTagCategoryChange,
  onAssociatedCategoryCheckboxChange,
  onCheckinBoardingCheckboxChange,
  onAssociatedCategoryDelete,
  onCreateOverride,
  classes,
}) => {
  if (categories.length === 0) {
    return (
      <Typography style={{ fontSize: '90%', marginBottom: '1rem', paddingLeft: '1rem' }}>
        No tag categories found.
      </Typography>
    );
  }

  return (
    <Box>
      {categories.map((cat, index) => (
        <CategoryCard
          key={cat.idx || cat.id || cat.category?.id || index}
          cat={cat}
          allCategories={categories}
          mode={mode}
          hasWritePerm={hasWritePerm}
          hasUpdateTagCategoriesPerm={hasUpdateTagCategoriesPerm}
          tripObject={tripObject}
          isParentTouched={isParentTouched}
          checkinCategoriesCount={checkinCategoriesCount}
          boardingCategoriesCount={boardingCategoriesCount}
          onAssociatedTagCategoryChange={onAssociatedTagCategoryChange}
          onAssociatedCategoryCheckboxChange={onAssociatedCategoryCheckboxChange}
          onCheckinBoardingCheckboxChange={onCheckinBoardingCheckboxChange}
          onAssociatedCategoryDelete={onAssociatedCategoryDelete}
          onCreateOverride={onCreateOverride}
          classes={classes}
        />
      ))}
    </Box>
  );
});

CardsView.displayName = 'CardsView';

// Individual Category Card
const CategoryCard = memo(({
  cat,
  allCategories,
  mode,
  hasWritePerm,
  hasUpdateTagCategoriesPerm,
  tripObject,
  isParentTouched,
  checkinCategoriesCount,
  boardingCategoriesCount,
  onAssociatedTagCategoryChange,
  onAssociatedCategoryCheckboxChange,
  onCheckinBoardingCheckboxChange,
  onAssociatedCategoryDelete,
  onCreateOverride,
  classes,
}) => {
  const [isOpen, setIsOpen] = useState(cat.is_new || false);
  const [isTouched, setIsTouched] = useState(false);

  const isOperatorDefault = cat.source === 'operator';
  // In trip or create mode, all operator default categories are read-only (inherited)
  const isReadOnly = (mode === 'trip' || mode === 'create') && isOperatorDefault;
  const canManageCategories = hasWritePerm && (mode === 'operator' || hasUpdateTagCategoriesPerm);
  const canEditFeatureFlags = mode === 'operator' ? hasWritePerm : hasUpdateTagCategoriesPerm;

  const handleChange = (ev, selectedCategory) => {
    setIsTouched(true);
    onAssociatedTagCategoryChange(ev, selectedCategory, cat.idx);
  };

  // Build API filters for category selection - only exclude trip-level categories, not operator defaults
  const tripLevelCategories = (allCategories || []).filter(c => c.source === 'trip');
  const allUsedIds = [...new Set(tripLevelCategories
    .filter(c => c.category?.id)
    .map(c => c.category.id.toString()))];
  const currentCategoryId = cat.category?.id?.toString();
  const excludeIds = currentCategoryId ? allUsedIds.filter(id => id !== currentCategoryId) : allUsedIds;

  const apiFilters = [];
  if (excludeIds.length > 0) {
    apiFilters.push({ field: 'id', op: 'not_in', value: excludeIds });
  }
  if (tripObject.is_bespoke) {
    apiFilters.push({ field: 'code', op: 'ne', value: 'departure' });
  }

  const cardClassName = `${classes.categoryCard} ${isReadOnly ? classes.operatorDefaultCard : classes.tripOverrideCard}`;

  return (
    <div className={cardClassName}>
      <Grid container spacing={1} alignItems="center" style={{ width: '100%' }}>
        <Grid item>
          <IconButton color="primary" onClick={() => setIsOpen(prev => !prev)}>
            {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Grid>
        <Grid item xs>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs>
              {cat.is_new && !cat.category?.id && !isReadOnly ? (
                <RemoteAutocomplete
                  resource="tour_tag_category"
                  label="Tag Category"
                  variant="filled"
                  size="small"
                  disabled={!canManageCategories}
                  disableClearable={true}
                  onChange={handleChange}
                  value={cat.category}
                  getOptionLabel={(opt) => opt.name}
                  apiFilters={apiFilters}
                  textFieldProps={{
                    onBlur: () => setIsTouched(true),
                    error: (isParentTouched || isTouched) && !cat.category?.id,
                    helperText: (isParentTouched || isTouched) && !cat.category?.id ? 'Please select a category' : undefined
                  }}
                />
              ) : (
                <Typography sx={{ fontWeight: 500, fontSize: '1rem' }}>
                  {cat.category?.name || 'Unknown Category'}
                </Typography>
              )}
            </Grid>
            {(mode === 'trip' || mode === 'create') && (
              <Grid item>
                <Chip
                  label={
                    isOperatorDefault
                      ? 'Inherited'
                      : (cat.isOverride ? 'Override' : 'Trip')
                  }
                  size="small"
                  color={isOperatorDefault ? 'warning' : 'primary'}
                  variant={isOperatorDefault ? 'filled' : 'filled'}
                  className={classes.sourceChip}
                />
              </Grid>
            )}
          </Grid>
        </Grid>
        <Grid item>
          {isReadOnly ? (
            <Tooltip title="Create trip-level override">
              <IconButton onClick={() => onCreateOverride(cat)} disabled={!canManageCategories}>
                <ContentCopyIcon color={canManageCategories ? 'primary' : 'disabled'} />
              </IconButton>
            </Tooltip>
          ) : (
            <DeleteIconButton allowed={canManageCategories} size="medium" fontSize="medium" onClick={() => onAssociatedCategoryDelete(cat.idx)} />
          )}
        </Grid>
      </Grid>
      {isOpen && (
        <Box sx={{ marginTop: '1rem', paddingLeft: '13px', opacity: isReadOnly ? 0.6 : 1 }}>
          <CategoryCheckboxes
            cat={{ ...cat, is_new: isReadOnly ? false : canEditFeatureFlags }}
            showDepartureKey={mode === 'operator'}
            checkinCategoriesCount={checkinCategoriesCount}
            boardingCategoriesCount={boardingCategoriesCount}
            onAssociatedCategoryCheckboxChange={isReadOnly ? () => {} : onAssociatedCategoryCheckboxChange}
            onCheckinBoardingCheckboxChange={isReadOnly ? () => {} : onCheckinBoardingCheckboxChange}
          />
        </Box>
      )}
    </div>
  );
});

CategoryCard.displayName = 'CategoryCard';

TripDefaultSettings2.propTypes = {
  mode: PropTypes.oneOf(['operator', 'trip', 'create']),
  hasWritePerm: PropTypes.bool,
  hasUpdateTagCategoriesPerm: PropTypes.bool,
  hideGeneralSettings: PropTypes.bool,
  locationSharingValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onLocationSharingChange: PropTypes.func,
  myTripsDisplayValue: PropTypes.bool,
  onMyTripsDisplayChange: PropTypes.func,
  hasGroupChatValue: PropTypes.bool,
  onHasGroupChatChange: PropTypes.func,
  excludeFromChatValue: PropTypes.bool,
  onExcludeFromChatChange: PropTypes.func,
  disableInternalChatValue: PropTypes.bool,
  onDisableInternalChatChange: PropTypes.func,

  operatorDefaultCategories: PropTypes.array,
  defaultsAssociatedTagCategories: PropTypes.array,
  onSetDefaultsAssociated: PropTypes.func,
  liveSyncOperatorId: PropTypes.number,
  onLiveSynced: PropTypes.func,
  formHasChanges: PropTypes.bool,

  trip: PropTypes.object,
  onTripChange: PropTypes.func,
  clientErrors: PropTypes.object,
  isParentTouched: PropTypes.any,
};

CardsView.propTypes = {
  categories: PropTypes.array.isRequired,
  mode: PropTypes.oneOf(['operator', 'trip', 'create']).isRequired,
  hasWritePerm: PropTypes.bool,
  hasUpdateTagCategoriesPerm: PropTypes.bool,
  tripObject: PropTypes.object.isRequired,
  isParentTouched: PropTypes.any,
  checkinCategoriesCount: PropTypes.number.isRequired,
  boardingCategoriesCount: PropTypes.number.isRequired,
  onAssociatedTagCategoryChange: PropTypes.func.isRequired,
  onAssociatedCategoryCheckboxChange: PropTypes.func.isRequired,
  onCheckinBoardingCheckboxChange: PropTypes.func.isRequired,
  onAssociatedCategoryDelete: PropTypes.func.isRequired,
  onCreateOverride: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired,
};

CategoryCard.propTypes = {
  cat: PropTypes.object.isRequired,
  allCategories: PropTypes.array,
  mode: PropTypes.oneOf(['operator', 'trip', 'create']).isRequired,
  hasWritePerm: PropTypes.bool,
  hasUpdateTagCategoriesPerm: PropTypes.bool,
  tripObject: PropTypes.object.isRequired,
  isParentTouched: PropTypes.any,
  checkinCategoriesCount: PropTypes.number.isRequired,
  boardingCategoriesCount: PropTypes.number.isRequired,
  onAssociatedTagCategoryChange: PropTypes.func.isRequired,
  onAssociatedCategoryCheckboxChange: PropTypes.func.isRequired,
  onCheckinBoardingCheckboxChange: PropTypes.func.isRequired,
  onAssociatedCategoryDelete: PropTypes.func.isRequired,
  onCreateOverride: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired,
};

export default memo(TripDefaultSettings2);
